import React, { useState } from 'react';
import { CheckCircle, Sparkles, Database, Shield, ShieldCheck, BarChart, ChevronDown } from 'lucide-react';
import type { SearchType } from '@/types';
import { formatCurrency } from '@/utils/searchValidation';
import { cn } from '@/lib/utils';
import { WALLET_PRICING, ONE_TIME_PRICING } from '@/config/pricing';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useQuery } from '@tanstack/react-query';
import { stripeAPI } from '@/services/stripeAPI';

interface SearchTypeSelectorProps {
  value: SearchType;
  onChange: (value: SearchType) => void;
  className?: string;
}

export const SearchTypeSelector: React.FC<SearchTypeSelectorProps> = ({ value, onChange, className }) => {
  const { isAuthenticated, user } = useCustomerAuth();
  const [expandedCard, setExpandedCard] = useState<SearchType>(value);

  // Fetch wallet balance for authenticated users
  const { data: walletBalance = 0 } = useQuery({
    queryKey: ['wallet-balance', user?.email],
    queryFn: async () => {
      if (!user?.email) return 0;
      return await stripeAPI.getWalletBalance(user.email);
    },
    enabled: isAuthenticated && !!user?.email,
  });

  // Use wallet pricing if authenticated with balance, otherwise one-time pricing
  const useWalletPricing = isAuthenticated && walletBalance > 0;
  const pricing = useWalletPricing ? WALLET_PRICING : ONE_TIME_PRICING;

  // Update expanded card when value prop changes
  React.useEffect(() => {
    setExpandedCard(value);
  }, [value]);

  const options = [
    {
      type: 'enrich' as SearchType,
      title: 'Contact Data Enrichment',
      price: pricing.enrich,
      icon: Database,
      features: [
        'Phone Numbers & Carriers',
        'Email Addresses',
        'Address History',
        'Relatives & Associates',
      ],
      popular: false,
    },
    {
      type: 'validate' as SearchType,
      title: 'Identity Validation',
      price: pricing.validate,
      icon: Shield,
      features: [
        'Trust Score & ID Confidence',
        'Fraud Risk Assessment',
        'Identity Verification',
        'First/Last Seen Dates',
        'Up to 5 items per submission',
      ],
      popular: false,
    },
    {
      type: 'enrich_validate' as SearchType,
      title: 'Complete Skip Trace',
      price: pricing.enrich_validate,
      icon: ShieldCheck,
      features: [
        'All Identity Validation Features',
        'All Contact Enrichment Features',
        'Complete Skip Trace Report',
        'Data Quality Scores',
      ],
      popular: false,
    },
    {
      type: 'scoring' as SearchType,
      title: 'CollectABILITY Scoring',
      price: pricing.scoring,
      icon: Sparkles,
      features: [
        'CollectABILITY Score (300-850)',
        'Financial Insights & Metrics',
        'Recovery Scenario Analysis',
        'Identity Verification',
      ],
      popular: false,
    },
    {
      type: 'scoring_enrich' as SearchType,
      title: 'Full Intelligence Report',
      price: pricing.scoring_enrich,
      icon: BarChart,
      features: [
        'Everything in Scoring',
        'Complete Contact Information',
        'Address History & Verification',
        'Phone Numbers & Email Addresses',
        'Financial Profile',
      ],
      popular: true,
    },
  ];

  const handleCardClick = (type: SearchType) => {
    // Toggle expansion and select the card
    if (expandedCard === type) {
      // If clicking already expanded card, keep it expanded but select it
      onChange(type);
    } else {
      // Expand new card and select it
      setExpandedCard(type);
      onChange(type);
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      {options.map((option) => {
        const Icon = option.icon;
        const isSelected = value === option.type;
        const isExpanded = expandedCard === option.type;

        return (
          <div
            key={option.type}
            className={cn(
              'relative rounded-lg border-2 transition-all overflow-hidden',
              isSelected
                ? 'border-collectability-blue bg-slate-100 shadow-md'
                : 'border-slate-200 bg-white hover:border-collectability-blue/50'
            )}
          >
            {/* Collapsed Header - Always Visible */}
            <button
              type="button"
              onClick={() => handleCardClick(option.type)}
              className="w-full p-4 flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3 flex-1">
                <div
                  className={cn(
                    'p-2 rounded-lg',
                    isSelected ? 'bg-collectability-blue text-white' : 'bg-slate-100 text-slate-600'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 text-base">{option.title}</h3>
                    {option.popular && (
                      <span className="px-2 py-0.5 bg-collectability-blue text-white text-xs font-semibold rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-xl font-bold text-collectability-blue">
                    {formatCurrency(option.price)}
                  </p>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-slate-400 transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </div>
              </div>
            </button>

            {/* Expanded Content - Features List */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-slate-200">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


